import { useSyncExternalStore } from "react";
import { useAuth } from "@/components/providers/auth-provider";

/**
 * Which currency a Learn price is shown in first (`LN-BUY01`, founder
 * 2026-10-08): taka for a visitor in Bangladesh, dollars for everyone else.
 *
 * A signed-in visitor is decided by the account's country, by the same rule the
 * checkout charges by (`docs/33` §4.1: empty reads as Bangladesh), so the page
 * never shows $19 to someone the checkout will ask for ৳1,999. A signed-out
 * visitor is decided by the device's time zone, as contest times are
 * (`features/contests/lib/display-zone.ts`): the Learn pages are ISR-cached,
 * so the server cannot answer per visitor.
 */
export type LearnPriceRegion = "bd" | "intl";

/** `Asia/Dacca` is the pre-2010 IANA spelling some older systems still report. */
const BANGLADESH_ZONES: ReadonlySet<string> = new Set(["Asia/Dhaka", "Asia/Dacca"]);

export function isBangladeshCountry(country: string | null | undefined): boolean {
  const c = (country ?? "").trim().toLowerCase();
  return c === "" || c === "bd" || c === "bangladesh";
}

export function learnPriceRegionFor(input: {
  signedIn: boolean;
  country?: string | null;
  deviceZone?: string | null;
}): LearnPriceRegion {
  if (input.signedIn) return isBangladeshCountry(input.country) ? "bd" : "intl";
  return input.deviceZone && BANGLADESH_ZONES.has(input.deviceZone) ? "bd" : "intl";
}

function deviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

/** A device's zone does not change under a page, so there is nothing to subscribe to. */
function subscribe(): () => void {
  return () => {};
}

/**
 * The server and the hydrating render show taka, the platform's home audience;
 * a visitor elsewhere re-renders to dollars straight after.
 */
function serverZone(): string {
  return "Asia/Dhaka";
}

export function useLearnPriceRegion(): LearnPriceRegion {
  const { user } = useAuth();
  const deviceZone = useSyncExternalStore(subscribe, deviceTimeZone, serverZone);
  return user
    ? learnPriceRegionFor({ signedIn: true, country: user.country })
    : learnPriceRegionFor({ signedIn: false, deviceZone });
}
